import React, { useState, useEffect, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { VisitorPageTracker } from './components/VisitorPageTracker';
import Login from './pages/Login';
import ForcePasswordChange from './pages/ForcePasswordChange';
// Pagine caricate lazy: riduce il bundle iniziale e velocizza il primo caricamento
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Leads = lazy(() => import('./pages/Leads'));
const LeadDetail = lazy(() => import('./pages/LeadDetail'));
const Projects = lazy(() => import('./pages/Projects'));
const ProjectDetail = lazy(() => import('./pages/ProjectDetail'));
const Invoices = lazy(() => import('./pages/Invoices'));
const Team = lazy(() => import('./pages/Team'));
const Users = lazy(() => import('./pages/Users'));
const Notifications = lazy(() => import('./pages/Notifications'));
const Calendar = lazy(() => import('./pages/Calendar'));
const Quotes = lazy(() => import('./pages/Quotes'));
const Integrations = lazy(() => import('./pages/Integrations'));
const ClientPortal = lazy(() => import('./pages/ClientPortal'));
const SalesAnalytics = lazy(() => import('./pages/SalesAnalytics'));
const CalcolatoreFinanziario = lazy(() => import('./pages/CalcolatoreFinanziario'));
const Contratti = lazy(() => import('./pages/Contratti'));
const ContrattiAnnullati = lazy(() => import('./pages/ContrattiAnnullati'));
const RiepilogoVendite = lazy(() => import('./pages/RiepilogoVendite'));
const Redditivita = lazy(() => import('./pages/Redditivita'));
const AdminSettings = lazy(() => import('./pages/AdminSettings'));
const AccessLog = lazy(() => import('./pages/AccessLog'));
const GuidaUtente = lazy(() => import('./pages/GuidaUtente'));
const Timbratura = lazy(() => import('./pages/Timbratura'));
const Ore = lazy(() => import('./pages/Ore'));
const HR = lazy(() => import('./pages/HR'));
const Assistenza = lazy(() => import('./pages/Assistenza'));
const Marketing = lazy(() => import('./pages/Marketing'));
const WhatsApp = lazy(() => import('./pages/WhatsApp'));
const ContattoPubblico = lazy(() => import('./pages/ContattoPubblico'));
const SquadreEsterne = lazy(() => import('./pages/SquadreEsterne'));
const Candidature = lazy(() => import('./pages/Candidature'));
const Listino = lazy(() => import('./pages/Listino'));
const Clausole = lazy(() => import('./pages/Clausole'));
const Isolamento = lazy(() => import('./pages/Isolamento'));
const Biblioteca = lazy(() => import('./pages/Biblioteca'));
const Provvigioni = lazy(() => import('./pages/Provvigioni'));
import { PWAInstallPrompt } from './components/PWAInstallPrompt';
import { hasFeature } from './utils/access';
import { getToken, getUser, setSession, updateStoredUser, clearSession, enforceSessionExpiry } from './utils/authStorage';
import './App.css';

const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
  </div>
);

// Error boundary to prevent white screens
class ErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { hasError: false, error: null }; }
  static getDerivedStateFromError(error) { return { hasError: true, error }; }
  componentDidCatch(error, info) { console.error('App Error:', error, info); }
  render() {
    if (this.state.hasError) {
      return (
        <div style={{ padding: 40, textAlign: 'center', fontFamily: 'system-ui' }}>
          <h2 style={{ color: '#991b1b' }}>Si e verificato un errore</h2>
          <p style={{ color: '#666', marginTop: 8 }}>Prova a ricaricare la pagina.</p>
          <button onClick={() => { 
            if ('caches' in window) caches.keys().then(n => n.forEach(k => caches.delete(k)));
            window.location.reload(true);
          }} style={{ marginTop: 16, padding: '12px 24px', background: '#0d7c66', color: 'white', border: 'none', borderRadius: 8, fontSize: 16, cursor: 'pointer' }}>
            Ricarica Pagina
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    enforceSessionExpiry();
    const token = getToken();
    const userData = getUser();

    if (token && userData) {
      setUser(userData);
      // Aggiorna ruolo/permessi dal server ad ogni caricamento
      fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/me`, { headers: { 'Authorization': `Bearer ${token}` } })
        .then(r => r.ok ? r.json() : null)
        .then(d => {
          if (d && d.id) {
            const merged = { ...userData, ruolo: d.ruolo, ruolo_reale: d.ruolo_reale, is_visitor: !!d.is_visitor, feature_overrides: d.feature_overrides || {}, first_login: d.first_login };
            setUser(merged);
            updateStoredUser(merged);
          }
        })
        .catch(() => {});
    }
    setLoading(false);
  }, []);

  const handleLogin = (userData, token, remember = false) => {
    setUser(userData);
    setSession(token, userData, remember);
  };

  const handlePasswordChanged = () => {
    const updated = { ...user, first_login: false };
    setUser(updated);
    updateStoredUser(updated);
  };

  const handleLogout = () => {
    setUser(null);
    clearSession();
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <ErrorBoundary>
    <Router>
      <VisitorPageTracker user={user} />
      {user && user.first_login ? (
        <ForcePasswordChange user={user} onDone={handlePasswordChanged} onLogout={handleLogout} />
      ) : (
      <Suspense fallback={<PageLoader />}>
      <Routes>
        <Route
          path="/contatto"
          element={<ContattoPubblico />}
        />
        <Route
          path="/login"
          element={!user ? <Login onLogin={handleLogin} /> : <Navigate to="/" />}
        />
        <Route
          path="/"
          element={user ? (user.ruolo === 'Cliente' ? <Navigate to="/client" /> : <Dashboard user={user} onLogout={handleLogout} />) : <Navigate to="/login" />}
        />
        <Route
          path="/leads"
          element={user ? (hasFeature(user, '/leads') ? <Leads user={user} onLogout={handleLogout} /> : <Navigate to="/" />) : <Navigate to="/login" />}
        />
        <Route
          path="/leads/:id"
          element={user ? <LeadDetail user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}
        />
        <Route
          path="/projects"
          element={user ? (hasFeature(user, '/projects') ? <Projects user={user} onLogout={handleLogout} /> : <Navigate to="/" />) : <Navigate to="/login" />}
        />
        <Route
          path="/projects/:id"
          element={user ? <ProjectDetail user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}
        />
        <Route
          path="/invoices"
          element={user ? (hasFeature(user, '/invoices') ? <Invoices user={user} onLogout={handleLogout} /> : <Navigate to="/" />) : <Navigate to="/login" />}
        />
        <Route
          path="/team"
          element={user ? (hasFeature(user, '/team') ? <Team user={user} onLogout={handleLogout} /> : <Navigate to="/" />) : <Navigate to="/login" />}
        />
        <Route
          path="/candidature"
          element={user ? (['Admin', 'Amministrativo'].includes(user.ruolo) ? <Candidature user={user} onLogout={handleLogout} /> : <Navigate to="/" />) : <Navigate to="/login" />}
        />
        <Route
          path="/users"
          element={user ? <Users user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}
        />
        <Route
          path="/notifications"
          element={user ? (hasFeature(user, '/notifications') ? <Notifications user={user} onLogout={handleLogout} /> : <Navigate to="/" />) : <Navigate to="/login" />}
        />
        <Route
          path="/calendar"
          element={user ? (hasFeature(user, '/calendar') ? <Calendar user={user} onLogout={handleLogout} /> : <Navigate to="/" />) : <Navigate to="/login" />}
        />
        <Route
          path="/quotes"
          element={user ? (hasFeature(user, '/quotes') ? <Quotes user={user} onLogout={handleLogout} /> : <Navigate to="/" />) : <Navigate to="/login" />}
        />
        <Route
          path="/integrations"
          element={user ? (hasFeature(user, '/integrations') ? <Integrations user={user} onLogout={handleLogout} /> : <Navigate to="/" />) : <Navigate to="/login" />}
        />
        <Route
          path="/analytics"
          element={user ? (hasFeature(user, '/analytics') ? <SalesAnalytics user={user} onLogout={handleLogout} /> : <Navigate to="/" />) : <Navigate to="/login" />}
        />
        <Route
          path="/calcolatore-finanziario"
          element={user ? (hasFeature(user, '/calcolatore-finanziario') ? <CalcolatoreFinanziario user={user} onLogout={handleLogout} /> : <Navigate to="/" />) : <Navigate to="/login" />}
        />
        <Route
          path="/contratti"
          element={user ? (hasFeature(user, '/contratti') ? <Contratti user={user} onLogout={handleLogout} /> : <Navigate to="/" />) : <Navigate to="/login" />}
        />
        <Route
          path="/contratti-annullati"
          element={user ? (hasFeature(user, '/contratti-annullati') ? <ContrattiAnnullati user={user} onLogout={handleLogout} /> : <Navigate to="/" />) : <Navigate to="/login" />}
        />
        <Route
          path="/riepilogo-vendite"
          element={user ? (hasFeature(user, '/riepilogo-vendite') ? <RiepilogoVendite user={user} onLogout={handleLogout} /> : <Navigate to="/" />) : <Navigate to="/login" />}
        />
        <Route
          path="/redditivita"
          element={user ? (user.ruolo === 'Admin' ? <Redditivita user={user} onLogout={handleLogout} /> : <Navigate to="/" />) : <Navigate to="/login" />}
        />
        <Route
          path="/listino"
          element={user ? (user.ruolo === 'Admin' ? <Listino user={user} onLogout={handleLogout} /> : <Navigate to="/" />) : <Navigate to="/login" />}
        />
        <Route
          path="/clausole"
          element={user ? (user.ruolo === 'Admin' ? <Clausole user={user} onLogout={handleLogout} /> : <Navigate to="/" />) : <Navigate to="/login" />}
        />
        <Route
          path="/isolamento"
          element={user ? (hasFeature(user, '/isolamento', ['Admin', 'Amministrativo', 'Commerciale', 'CapoGruppo']) ? <Isolamento user={user} onLogout={handleLogout} /> : <Navigate to="/" />) : <Navigate to="/login" />}
        />
        <Route
          path="/biblioteca"
          element={user ? (hasFeature(user, '/biblioteca', ['Admin', 'Amministrativo', 'Commerciale', 'CapoGruppo', 'Caposquadra', 'Operativo']) ? <Biblioteca user={user} onLogout={handleLogout} /> : <Navigate to="/" />) : <Navigate to="/login" />}
        />
        <Route
          path="/provvigioni"
          element={user ? (hasFeature(user, '/provvigioni', ['Admin', 'Amministrativo', 'Commerciale', 'CapoGruppo']) ? <Provvigioni user={user} onLogout={handleLogout} /> : <Navigate to="/" />) : <Navigate to="/login" />}
        />
        <Route
          path="/client"
          element={user ? (user.ruolo === 'Cliente' ? <ClientPortal user={user} onLogout={handleLogout} /> : <Navigate to="/" />) : <Navigate to="/login" />}
        />
        <Route
          path="/admin/settings"
          element={user ? (user.ruolo === 'Admin' ? <AdminSettings user={user} onLogout={handleLogout} /> : <Navigate to="/" />) : <Navigate to="/login" />}
        />
        <Route
          path="/access-log"
          element={user ? (hasFeature(user, '/access-log') ? <AccessLog user={user} onLogout={handleLogout} /> : <Navigate to="/" />) : <Navigate to="/login" />}
        />
        <Route
          path="/guida"
          element={user ? <GuidaUtente user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}
        />
        <Route
          path="/timbratura"
          element={user ? (hasFeature(user, '/timbratura') ? <Timbratura user={user} onLogout={handleLogout} /> : <Navigate to="/" />) : <Navigate to="/login" />}
        />
        <Route
          path="/ore"
          element={user ? (hasFeature(user, '/ore') ? <Ore user={user} onLogout={handleLogout} /> : <Navigate to="/" />) : <Navigate to="/login" />}
        />
        <Route
          path="/hr"
          element={user ? (hasFeature(user, '/hr') ? <HR user={user} onLogout={handleLogout} /> : <Navigate to="/" />) : <Navigate to="/login" />}
        />
        <Route
          path="/assistenza"
          element={user ? (hasFeature(user, '/assistenza') ? <Assistenza user={user} onLogout={handleLogout} /> : <Navigate to="/" />) : <Navigate to="/login" />}
        />
        <Route
          path="/marketing"
          element={user ? (hasFeature(user, '/marketing') ? <Marketing user={user} onLogout={handleLogout} /> : <Navigate to="/" />) : <Navigate to="/login" />}
        />
        <Route
          path="/whatsapp"
          element={user ? (hasFeature(user, '/whatsapp') ? <WhatsApp user={user} onLogout={handleLogout} /> : <Navigate to="/" />) : <Navigate to="/login" />}
        />
        <Route
          path="/squadre-esterne"
          element={user ? (hasFeature(user, '/squadre-esterne') ? <SquadreEsterne user={user} onLogout={handleLogout} /> : <Navigate to="/" />) : <Navigate to="/login" />}
        />
        <Route path="*" element={<Navigate to={user ? '/' : '/login'} />} />
      </Routes>
      </Suspense>
      )}
      <PWAInstallPrompt />
    </Router>
    </ErrorBoundary>
  );
}

export default App;
